import { useQuery } from '@apollo/client';

import transformData from 'crehana-utils/transformData';

import { createUrlWithSourceQueryParams } from 'Jsx/Analytics/Amplitude';

import { addSearchParamsToCourseUrl } from '@crehana/web/dist/MainMenu/utils';
import parseSearchCourse from '@crehana/web/dist/MainMenu/parsers/parseSearchCourse';

import { SearchLandingCoursesQueryVariables as SearchVariables } from '../graphql/types/SearchLandingCoursesQuery';
import SEARCH_COURSES_QUERY from '../graphql/SearchLandingCoursesQuery.v4.graphql';

import { SearchResponse, SearchCourse } from '../types';
import {
  SEARCH_LANDING_AMPLITUDE_PAGE_NAME,
  COURSES_PER_PAGE,
} from '../constants';

const useSearchCoursesQuery = ({ searchTerm }: { searchTerm: string }) => {
  const variables = { searchTerm, index: 'course', first: COURSES_PER_PAGE };

  const { data, loading, fetchMore } = useQuery<
    SearchResponse,
    SearchVariables
  >(SEARCH_COURSES_QUERY, {
    variables,
    skip: !searchTerm,
    fetchPolicy: 'network-only',
    context: { clientName: 'v4' },
  });

  const onFetchMore = () => {
    fetchMore({
      variables: { ...variables, after: data?.search?.pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (prev?.search?.edges && fetchMoreResult?.search?.edges) {
          return {
            search: {
              ...prev.search,
              ...fetchMoreResult.search,
              edges: [
                ...prev.search.edges,
                // Include new result filtering possible duplicates
                ...fetchMoreResult.search.edges.filter(
                  newItem =>
                    !prev.search?.edges.find(
                      prevItem =>
                        prevItem?.node?.source.originalId ===
                        newItem?.node?.source.originalId,
                    ),
                ),
              ],
            },
          };
        }
        return prev;
      },
    });
  };

  const results: SearchCourse[] = data?.search
    ? transformData(data.search)
        .filter(node => !!node.source)
        .map((node, index) => {
          const courseNode = node.source;
          const course = parseSearchCourse({
            course: courseNode,
            amplitudePageName: SEARCH_LANDING_AMPLITUDE_PAGE_NAME,
            urlWithAmplitudeSource: courseNode?.url
              ? createUrlWithSourceQueryParams({
                  url: courseNode.url.relativeUrl,
                  source: SEARCH_LANDING_AMPLITUDE_PAGE_NAME,
                  sourceDetail: SEARCH_LANDING_AMPLITUDE_PAGE_NAME,
                })
              : '',
          });

          return {
            ...course,
            price: courseNode.price,
            url: addSearchParamsToCourseUrl(course.url as string, {
              positionSelected: index,
              id: course.originalId,
              keyword: searchTerm,
              modelName: data.search?.modelName as string,
              productName: course?.title as string,
            }),
            isSavedCourse: !!courseNode.isSavedCourse,
          };
        })
    : [];

  return {
    totalResults: data?.search?.totalSearch || 0,
    results,
    loading,
    hasMore: !!data?.search?.pageInfo.hasNextPage,
    onFetchMore,
  };
};

export default useSearchCoursesQuery;
